import React from 'react';
import loadable from '@loadable/component';
import { useLocation } from 'react-router-dom';

import {
  captureCfOrder,
  hasAttemptedCfOrder,
  readCfOrderFromSearch,
  rememberAttemptedCfOrder,
} from '../util/cfOrder';
import { notifyAccountSetup } from '../util/api';
import getPageDataLoadingAPI from '../containers/pageDataLoadingAPI';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';
import PreviewResolverPage from '../containers/PreviewResolverPage/PreviewResolverPage';
// Story 3.22 — rendered SYNCHRONOUSLY on the SSR pass, in place of the
// discarded redirect, for the signed-out ClickFunnels arrival. NOT a loadable:
// a lazy chunk is exactly what produced the ~1s of blank white screen.
import NewListingCfOrderPrompt from './NewListingCfOrderPrompt/NewListingCfOrderPrompt';

// routeConfiguration needs to initialize containers first
// Otherwise, components will import form container eventually and
// at that point css bundling / imports will happen in wrong order.
import { NamedRedirect } from '../components';

const pageDataLoadingAPI = getPageDataLoadingAPI();

const BillingSubscriptionsPage = loadable(() => import(/* webpackChunkName: "BillingSubscriptionsPage" */ '../containers/BillingSubscriptionsPage/BillingSubscriptionsPage'));
const AuthenticationPage = loadable(() => import(/* webpackChunkName: "AuthenticationPage" */ '../containers/AuthenticationPage/AuthenticationPage'));
const CheckoutPage = loadable(() => import(/* webpackChunkName: "CheckoutPage" */ '../containers/CheckoutPage/CheckoutPage'));
const CMSPage = loadable(() => import(/* webpackChunkName: "CMSPage" */ '../containers/CMSPage/CMSPage'));
const ContactDetailsPage = loadable(() => import(/* webpackChunkName: "ContactDetailsPage" */ '../containers/ContactDetailsPage/ContactDetailsPage'));
const ContactPage = loadable(() => import(/* webpackChunkName: "ContactPage" */ '../containers/ContactPage/ContactPage'));
const EditListingPage = loadable(() => import(/* webpackChunkName: "EditListingPage" */ '../containers/EditListingPage/EditListingPage'));
const InquiryContactConfirmPage = loadable(() => import(/* webpackChunkName: "InquiryContactConfirmPage" */ '../containers/InquiryContactConfirmPage/InquiryContactConfirmPage'));
const MissingTextsHelpPage = loadable(() => import(/* webpackChunkName: "MissingTextsHelpPage" */ '../containers/MissingTextsHelpPage/MissingTextsHelpPage'));
const ClaimSlotPage = loadable(() => import(/* webpackChunkName: "ClaimSlotPage" */ '../containers/ClaimSlotPage/ClaimSlotPage'));
const EmailVerificationPage = loadable(() => import(/* webpackChunkName: "EmailVerificationPage" */ '../containers/EmailVerificationPage/EmailVerificationPage'));
const FavoriteListingsPage = loadable(() => import(/* webpackChunkName: "FavoriteListingsPage" */ '../containers/FavoriteListingsPage/FavoriteListingsPage'));
const InboxPage = loadable(() => import(/* webpackChunkName: "InboxPage" */ '../containers/InboxPage/InboxPage'));
const MakeOfferPage = loadable(() => import(/* webpackChunkName: "MakeOfferPage" */ '../containers/MakeOfferPage/MakeOfferPage'));
// Homepage is a custom-coded container (replaces the PageBuilder LandingPage).
// The route name stays 'LandingPage' (Decision 4) so redirects/logout keep working.
const LandingPage = loadable(() => import(/* webpackChunkName: "HomePage" */ '../containers/HomePage/HomePage'));
const ListingPageCoverPhoto = loadable(() => import(/* webpackChunkName: "ListingPageCoverPhoto" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCoverPhoto'));
const ListingPageCarousel = loadable(() => import(/* webpackChunkName: "ListingPageCarousel" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCarousel'));
const ManageListingsPage = loadable(() => import(/* webpackChunkName: "ManageListingsPage" */ '../containers/ManageListingsPage/ManageListingsPage'));
const ManageAccountPage = loadable(() => import(/* webpackChunkName: "ManageAccountPage" */ '../containers/ManageAccountPage/ManageAccountPage'));
const PasswordChangePage = loadable(() => import(/* webpackChunkName: "PasswordChangePage" */ '../containers/PasswordChangePage/PasswordChangePage'));
const PasswordRecoveryPage = loadable(() => import(/* webpackChunkName: "PasswordRecoveryPage" */ '../containers/PasswordRecoveryPage/PasswordRecoveryPage'));
const PasswordResetPage = loadable(() => import(/* webpackChunkName: "PasswordResetPage" */ '../containers/PasswordResetPage/PasswordResetPage'));
const PrivacyPolicyPage = loadable(() => import(/* webpackChunkName: "PrivacyPolicyPage" */ '../containers/PrivacyPolicyPage/PrivacyPolicyPage'));
const ProfilePage = loadable(() => import(/* webpackChunkName: "ProfilePage" */ '../containers/ProfilePage/ProfilePage'));
const ProfileSettingsPage = loadable(() => import(/* webpackChunkName: "ProfileSettingsPage" */ '../containers/ProfileSettingsPage/ProfileSettingsPage'));
const RequestQuotePage = loadable(() => import(/* webpackChunkName: "RequestQuotePage" */ '../containers/RequestQuotePage/RequestQuotePage'));
const SearchPageWithMap = loadable(() => import(/* webpackChunkName: "SearchPageWithMap" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithMap'));
const SearchPageWithGrid = loadable(() => import(/* webpackChunkName: "SearchPageWithGrid" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithGrid'));
const TermsOfServicePage = loadable(() => import(/* webpackChunkName: "TermsOfServicePage" */ '../containers/TermsOfServicePage/TermsOfServicePage'));
const TransactionPage = loadable(() => import(/* webpackChunkName: "TransactionPage" */ '../containers/TransactionPage/TransactionPage'));
const NoAccessPage = loadable(() => import(/* webpackChunkName: "NoAccessPage" */ '../containers/NoAccessPage/NoAccessPage'));

// Styleguide helps you to review current components and develop new ones
const StyleguidePage = loadable(() => import(/* webpackChunkName: "StyleguidePage" */ '../containers/StyleguidePage/StyleguidePage'));

export const ACCOUNT_SETTINGS_PAGES = [
  'ContactDetailsPage',
  'PasswordChangePage',
  'BillingSubscriptionsPage',
  'ManageAccountPage'
];

// https://en.wikipedia.org/wiki/Universally_unique_identifier#Nil_UUID
const draftId = '00000000-0000-0000-0000-000000000000';
const draftSlug = 'draft';

/**
 * Story 3.11 (AC1.3) — `/l/new` is where the ClickFunnels confirmation-page
 * button lands, so it is the first chance to see `?cf_order=`. Capture it, then
 * render the IDENTICAL redirect this route has always rendered — the target and
 * params are unchanged.
 *
 * Capture runs for the authenticated case too: an already-signed-in buyer
 * arriving with `?cf_order=` is a real shape, and this component renders for
 * them as well (the auth gate only intercepts the signed-out case).
 */
export const NewListingRedirect = () => {
  // ── Story 3.21 — THE READ HAPPENS AT RENDER TIME, FROM THE ROUTER ────────
  //
  // 🔴 WHY THIS IS NOT AN EFFECT ANY MORE, AND WHY IT IS NOT `window` ANY MORE.
  //
  // <NamedRedirect> below is a CHILD, and its <Redirect> calls history.replace
  // from componentDidMount — a COMMIT-phase lifecycle. `history` v4.9.0
  // normalises an absent `search` to '', createPath then writes a bare href,
  // and window.location.search becomes '' SYNCHRONOUSLY inside that commit.
  // Children commit before parents, so:
  //
  //    render body ............................ '?cf_order=QoLxnq'   ✅
  //    useLayoutEffect (parent, post-child) ... ''                   ❌
  //    useEffect (passive) — WHERE 3.19 READ .. ''                   ❌
  //
  // Both reads below used to sit in that passive effect, so BOTH received ''.
  // Story 3.11's capture never wrote anything on this path and Story 3.19's
  // consumer never fired. `useLayoutEffect` does NOT fix it — only the render
  // phase still holds the param. Measured, not reasoned.
  //
  // ID-1: read from the ROUTER's render-time location, which react-router
  // produces during render and history.replaceState cannot mutate. This is the
  // same source AuthenticationPage.js:342 uses on the signup path that works.
  // 🔒 ONE read of the search string; both consumers below are fed from it, so
  // a fix cannot repair one reader and leave the other on a stale source.
  const { search } = useLocation();

  // ID-2: KEEP the SSR guard. `/l/new` is `auth: true` and server/dataLoader.js
  // skips auth routes, so the server never renders this today — but the reason
  // it is safe lives in a DIFFERENT file, and notifyAccountSetup calls
  // window.fetch unguarded, which server-side is a ReferenceError thrown from a
  // render, i.e. a blank page on a route that must never blank.
  if (typeof window !== 'undefined') {
    captureCfOrder(search);

    // ID-2 (3.19): THIS navigation's own param, never the stored value and
    // never `sessionStorage`. `captureCfOrder`'s return above is
    // first-write-wins and would hand back TK's FIRST order on his second
    // arrival. /l/new is EVERY seller's entry point, so a fallback to stored
    // state would fire the money path on an ordinary "create a listing".
    //
    // ID-4 (3.19): FIRE-AND-FORGET. Nothing the buyer waits on may await this.
    // The <NamedRedirect> below renders on the same tick it does today, on
    // every input — a 429, an { ok: false }, a timeout, a rejection, a call
    // that never settles. `Promise.resolve().then(...)` also contains a
    // synchronous throw, and the `.catch` is what keeps a rejection from
    // becoming an unhandled one.
    //
    // ID-1 (3.19): the attach is SILENT. No confirmation, no interstitial. The
    // order id in the URL IS the binding, exactly as with the claim link.
    const orderId = readCfOrderFromSearch(search);
    if (orderId && !hasAttemptedCfOrder(orderId)) {
      // 🔴 ID-3 — THIS ORDERING IS LOAD-BEARING, AND MORE SO THAN IT WAS.
      // Recorded BEFORE the call. React does not promise to render exactly
      // once: this component is rendered by a redux-connected container whose
      // own componentDidMount dispatches an action Routes.js:145-146 describes
      // as generating "an unnecessary update", so a second render is real and
      // not hypothetical. React discards a re-invoked render's OUTPUT; it
      // cannot un-write a synchronous global side effect — so invoke #2 reads
      // the set invoke #1 just wrote and short-circuits here.
      // Moving this INTO the promise chain defers it past the second render
      // and fires two POSTs. Suppression is POLITENESS — idempotency is owned
      // server-side by attachHeldGrantsForUser (orderSpent, ON CONFLICT DO
      // NOTHING) — but this ordering is what makes a render-phase read
      // single-fire, and it is why the read could move here at all.
      rememberAttemptedCfOrder(orderId);
      Promise.resolve()
        .then(() => notifyAccountSetup(orderId, 'authenticated_arrival'))
        .catch(() => {});
    }
  }

  return (
    <NamedRedirect
      name="EditListingPage"
      params={{ slug: draftSlug, id: draftId, type: 'new', tab: 'details' }}
    />
  );
};

const RedirectToLandingPage = () => <NamedRedirect name="LandingPage" />;

// NOTE: Most server-side endpoints are prefixed with /api. Requests to those
// endpoints are indended to be handled in the server instead of the browser and
// they will not render the application. So remember to avoid routes starting
// with /api and if you encounter clashing routes see server/index.js if there's
// a conflicting route defined there.

// Our routes are exact by default.
// See behaviour from Routes.js where Route is created.
const routeConfiguration = (layoutConfig, accessControlConfig) => {
  const isSearchPageWithMap = layoutConfig.searchPage?.variantType === 'map';
  const SearchPage = isSearchPageWithMap ? SearchPageWithMap : SearchPageWithGrid;
  const ListingPage = layoutConfig.listingPage?.variantType === 'carousel' 
    ? ListingPageCarousel 
    : ListingPageCoverPhoto;

  const isPrivateMarketplace = accessControlConfig?.marketplace?.private === true;
  const authForPrivateMarketplace = isPrivateMarketplace ? { auth: true } : {};
  
  return [
    {
      path: '/',
      name: 'LandingPage',
      component: LandingPage,
      loadData: pageDataLoadingAPI.LandingPage.loadData,
    },
    {
      path: '/p/:pageId',
      name: 'CMSPage',
      component: CMSPage,
      loadData: pageDataLoadingAPI.CMSPage.loadData,
    },
    // NOTE: when the private marketplace feature is enabled, the '/s' route is disallowed by the robots.txt resource.
    // If you add new routes that start with '/s*' (e.g. /support), you should add them to the robotsPrivateMarketplace.txt file.
    {
      path: '/s',
      name: 'SearchPage',
      ...authForPrivateMarketplace,
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
      prioritizeMapLibraryLoading: isSearchPageWithMap,
    },
    {
      path: '/s/:listingType',
      name: 'SearchPageWithListingType',
      ...authForPrivateMarketplace,
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
      prioritizeMapLibraryLoading: isSearchPageWithMap,
    },
    {
      path: '/l',
      name: 'ListingBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/l/:slug/:id',
      name: 'ListingPage',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
      prioritizeMapLibraryLoading: true,
    },
    {
      path: '/l/:slug/:id/make-offer',
      name: 'MakeOfferPage',
      auth: true,
      component: MakeOfferPage,
      loadData: pageDataLoadingAPI.MakeOfferPage.loadData,
    },
    {
      path: '/l/:slug/:id/request-quote',
      name: 'RequestQuotePage',
      auth: true,
      component: RequestQuotePage,
      extraProps: { mode: 'request-quote' },
      loadData: pageDataLoadingAPI.RequestQuotePage.loadData,
    },
    {
      path: '/l/:slug/:id/checkout',
      name: 'CheckoutPage',
      auth: true,
      component: CheckoutPage,
      setInitialValues: pageDataLoadingAPI.CheckoutPage.setInitialValues,
    },
    {
      path: '/l/:slug/:id/:variant',
      name: 'ListingPageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
      prioritizeMapLibraryLoading: true,
    },
    {
      path: '/l/new',
      name: 'NewListingPage',
      auth: true,
      component: NewListingRedirect,
      // Story 3.22 — 🔴 THE ONLY ROUTE IN THIS FILE THAT MAY DECLARE THIS, and
      // a reviewer should treat a second occurrence as a defect. It tells
      // `Routes.js`'s shared unauthorized arm to render this component instead
      // of the `<NamedRedirect>` WHEN — and only when — the signed-out arrival
      // also carries a well-formed `cf_order`. The 401 is unaffected:
      // `staticContext.unauthorized` is set upstream and unconditionally.
      unauthorizedComponent: NewListingCfOrderPrompt,
    },
    {
      path: '/l/:slug/:id/:type/:tab',
      name: 'EditListingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/:type/:tab/:returnURLType',
      name: 'EditListingStripeOnboardingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },

    // Canonical path should be after the `/l/new` path since they
    // conflict and `new` is not a valid listing UUID.
    {
      path: '/l/:id',
      name: 'ListingPageCanonical',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
      prioritizeMapLibraryLoading: true,
    },
    {
      path: '/u',
      name: 'ProfileBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/u/:id',
      name: 'ProfilePage',
      ...authForPrivateMarketplace,
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    {
      path: '/u/:id/:variant',
      name: 'ProfilePageVariant',
      auth: true,
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    {
      path: '/profile-settings',
      name: 'ProfileSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ProfileSettingsPage,
    },
    {
      path: '/favorites',
      name: 'FavoriteListingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: FavoriteListingsPage,
      loadData: pageDataLoadingAPI.FavoriteListingsPage.loadData,
    },

    // Note: authenticating with IdP (e.g. Facebook) expects that /login path exists
    // so that in the error case users can be redirected back to the LoginPage
    // In case you change this, remember to update the route in server/api/auth/loginWithIdp.js
    {
      path: '/login',
      name: 'LoginPage',
      component: AuthenticationPage,
      extraProps: { tab: 'login' },
    },
    {
      path: '/signup',
      name: 'SignupPage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/signup/:userType',
      name: 'SignupForUserTypePage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/confirm',
      name: 'ConfirmPage',
      component: AuthenticationPage,
      extraProps: { tab: 'confirm' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/recover-password',
      name: 'PasswordRecoveryPage',
      component: PasswordRecoveryPage,
    },
    {
      // Story 4.6 — the inquiry-contact confirmation page.
      //
      // The path is SHORT ON PURPOSE. It is carried in a cold SMS to a
      // non-user, and the AC7.1 segment budget is computed against a 52-char
      // link (origin + '/c/' + a 22-char token). Lengthening this path eats
      // directly into the copy budget and can push the invite from one SMS
      // segment to two, which measurably raises the odds of carrier filtering
      // — and a filtered invite is a feature that silently does not work.
      //
      // UNAUTHENTICATED by design: no `auth: true`, no authPage. The reader
      // has no account and never gets one; the token is the credential.
      path: '/c/:token',
      name: 'InquiryContactConfirmPage',
      component: InquiryContactConfirmPage,
    },
    {
      // Story 5.43 (D10, ID-17) — linked from every start-of-conversation
      // email: where a spam-filtered first text hides, on iPhone and Android.
      //
      // UNAUTHENTICATED by design: no `auth: true`. The reader may be a seller,
      // a buyer or an inquiry contact, opening it from an email, signed in or
      // not. The path is fixed — tph-api builds it as
      // `${MARKETPLACE_BASE_URL}/help/missing-texts` (relayEmailNotices).
      path: '/help/missing-texts',
      name: 'MissingTextsHelpPage',
      component: MissingTextsHelpPage,
    },
    {
      // Story 3.15 — the will-call claim page. The token in this path IS the
      // purchase binding: possession of it proves control of the inbox that
      // received the checkout receipt, which is materially stronger evidence
      // than the CF order-id path accepts.
      //
      // ⚠️ NO `auth: true`, NO authPage, AND THAT IS THE WHOLE DESIGN. The page
      // renders for signed-out and signed-in alike and branches itself. Gating
      // it behind auth would lock out precisely the cohort it was written for:
      // the buyer who paid, closed the tab, and has no account yet — or has one
      // under an address she cannot remember. She must be able to reach this
      // page first and choose her identity second.
      //
      // Unlike /c/:token above, path length is NOT a constraint here — this
      // link travels by email, not SMS, so there is no segment budget. The path
      // is spelled out for legibility in an inbox.
      path: '/claim/:token',
      name: 'ClaimSlotPage',
      component: ClaimSlotPage,
    },
    {
      path: '/inbox',
      name: 'InboxBasePage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="InboxPage" params={{ tab: 'sales' }} />,
    },
    {
      path: '/inbox/:tab',
      name: 'InboxPage',
      auth: true,
      authPage: 'LoginPage',
      component: InboxPage,
      loadData: pageDataLoadingAPI.InboxPage.loadData,
    },
    {
      path: '/order/:id',
      name: 'OrderDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'customer' },
      loadData: (params, ...rest) =>
        pageDataLoadingAPI.TransactionPage.loadData({ ...params, transactionRole: 'customer' }, ...rest),
      setInitialValues: pageDataLoadingAPI.TransactionPage.setInitialValues,
    },
    {
      path: '/order/:id/details',
      name: 'OrderDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="OrderDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/sale/:id',
      name: 'SaleDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'provider' },
      loadData: pageDataLoadingAPI.TransactionPage.loadData,
    },
    {
      path: '/sale/:id/details',
      name: 'SaleDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="SaleDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/listings',
      name: 'ManageListingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ManageListingsPage,
      loadData: pageDataLoadingAPI.ManageListingsPage.loadData,
    },
    {
      path: '/account',
      name: 'AccountSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="ContactDetailsPage" />,
    },
    {
      path: '/account/contact-details',
      name: 'ContactDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ContactDetailsPage,
      loadData: pageDataLoadingAPI.ContactDetailsPage.loadData,
    },
    {
      path: '/account/change-password',
      name: 'PasswordChangePage',
      auth: true,
      authPage: 'LoginPage',
      component: PasswordChangePage,
    },
    {
      path: '/account/billing',
      name: 'BillingSubscriptionsPage',
      auth: true,
      authPage: 'LoginPage',
      component: BillingSubscriptionsPage,
    },
    // Payout details and Payment methods were removed from the account family
    // (operator decision: TPH runs no on-platform transactions). The routes
    // stay as graceful redirects for old bookmarks.
    {
      path: '/account/payments',
      name: 'StripePayoutPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="ContactDetailsPage" />,
    },
    {
      path: '/account/payments/:returnURLType',
      name: 'StripePayoutOnboardingPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="ContactDetailsPage" />,
    },
    {
      path: '/account/payment-methods',
      name: 'PaymentMethodsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="ContactDetailsPage" />,
    },
    {
      path: '/account/manage',
      name: 'ManageAccountPage',
      auth: true,
      authPage: 'LoginPage',
      component: ManageAccountPage,
    },
    {
      path: '/contact',
      name: 'ContactPage',
      component: ContactPage,
      loadData: pageDataLoadingAPI.ContactPage.loadData,
    },
    {
      path: '/terms-of-service',
      name: 'TermsOfServicePage',
      component: TermsOfServicePage,
      loadData: pageDataLoadingAPI.TermsOfServicePage.loadData,
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicyPage',
      component: PrivacyPolicyPage,
      loadData: pageDataLoadingAPI.PrivacyPolicyPage.loadData,
    },
    {
      path: '/styleguide',
      name: 'Styleguide',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/g/:group',
      name: 'StyleguideGroup',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component',
      name: 'StyleguideComponent',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example',
      name: 'StyleguideComponentExample',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example/raw',
      name: 'StyleguideComponentExampleRaw',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
      extraProps: { raw: true },
    },
    {
      path: '/no-:missingAccessRight',
      name: 'NoAccessPage',
      component: NoAccessPage,
    },
    {
      path: '/notfound',
      name: 'NotFoundPage',
      component: props => <NotFoundPage {...props} />,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /reset-password endpoint
    {
      path: '/reset-password',
      name: 'PasswordResetPage',
      component: PasswordResetPage ,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /verify-email endpoint
    {
      path: '/verify-email',
      name: 'EmailVerificationPage',
      auth: true,
      authPage: 'LoginPage',
      component: EmailVerificationPage,
      loadData: pageDataLoadingAPI.EmailVerificationPage.loadData,
    },
    // Do not change this path!
    //
    // The API expects that the application implements /preview endpoint
    {
      path: '/preview',
      name: 'PreviewResolverPage',
      component: PreviewResolverPage ,
    },
  ];
};

export default routeConfiguration;
